import Lottie from "lottie-react";
import { useEffect, useMemo, useState } from "react";
import {
    normalizeLottieAnimation,
    parseLottieColor,
    resolveLottieColorInput,
    resolveLottieStrokeWidth,
} from "./normalizeLottieAnimation";

// Import all animations
import addUserAnimation from "./add-user.json";
import arrowBarChartAnimation from "./arrow-bar-chart.json";
import barChartAnimation from "./bar-chart.json";
import chat from "./chat.json";
import clipboard from "./clipboard.json";
import copy from "./copy.json";
import customerAnimation from "./customer.json";
import diagramAnimation from "./diagram.json";
import diaryAnimation from "./diary.json";
import download from "./download.json";
import edit from "./edit.json";
import hammer from "./hammer.json";
import heart from "./heartbeat.json";
import helmet from "./helmet.json";
import key from "./key.json";
import letterAnimation from "./letter.json";
import lock from "./lock.json";
import logout from "./logout.json";
import medicine from "./medicine.json";
import menu from "./menu.json";
import notebook from "./notebook.json";
import plus from "./plus.json";
import profileAnimation from "./profile.json";
import question from "./question.json";
import sendAnimation from "./send-animation.json";
import settings from "./settings.json";
import socialMediaAnimation from "./social-media.json";
import societyAnimation from "./society.json";
import stethoscope from "./stethoscope.json";
import tableAnimation from "./table-animation.json";
import verified from "./verified.json";
import view from "./view.json";

// Animation mapping
const animations = {
    "add-user": addUserAnimation,
    "arrow-bar-chart": arrowBarChartAnimation,
    "bar-chart": barChartAnimation,
    customer: customerAnimation,
    diagram: diagramAnimation,
    diary: diaryAnimation,
    letter: letterAnimation,
    profile: profileAnimation,
    send: sendAnimation,
    "social-media": socialMediaAnimation,
    society: societyAnimation,
    table: tableAnimation,
    hammer: hammer,
    logout: logout,
    "question-speech-bubble": question,
    question: question,
    key: key,
    stethoscope: stethoscope,
    heart: heart,
    notebook: notebook,
    verified: verified,
    medicine: medicine,
    helmet: helmet,
    lock: lock,
    plus: plus,
    chat: chat,
    copy: copy,
    download: download,
    edit: edit,
    view: view,
    settings: settings,
    clipboard: clipboard,
    menu: menu,
} as const;

export type AnimationType = keyof typeof animations;

export const ANIMATION_TYPES = Object.keys(animations) as AnimationType[];

function useAppDarkMode() {
    const [isDark, setIsDark] = useState(
        () => typeof document !== "undefined" && document.documentElement.classList.contains("dark")
    );

    useEffect(() => {
        const root = document.documentElement;
        const sync = () => setIsDark(root.classList.contains("dark"));
        sync();
        const observer = new MutationObserver(sync);
        observer.observe(root, { attributes: true, attributeFilter: ["class"] });
        return () => observer.disconnect();
    }, []);

    return isDark;
}

interface AnimatedIconProps {
    animation: AnimationType;
    isHovered?: boolean;
    className?: string;
    size?: number;
    strokeWidth?: number;
    color?: string;
}

const AnimatedIcon = ({
    animation,
    isHovered = false,
    className,
    size = 28,
    color,
    strokeWidth,
}: AnimatedIconProps) => {
    const isDark = useAppDarkMode();
    const source = animations[animation];
    const animationData = useMemo(
        () =>
            source
                ? normalizeLottieAnimation(
                      source,
                      parseLottieColor(resolveLottieColorInput(color, isDark)),
                      resolveLottieStrokeWidth(strokeWidth)
                  )
                : null,
        [source, color, strokeWidth, isDark]
    );

    if (!animationData) {
        console.warn(`Animation "${animation}" not found`);
        return null;
    }

    return (
        <Lottie
            animationData={animationData}
            loop={isHovered}
            autoplay={isHovered}
            className={className}
            style={{
                width: `${size}px`,
                height: `${size}px`,
            }}
        />
    );
};

export default AnimatedIcon;
